import { ComponentProps, useEffect, useRef, useState } from 'react';
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';

export type BoundaryTier = 'app' | 'route' | 'feature';

const tierToLevel = {
  app: 'fatal',
  route: 'fatal',
  feature: 'error',
} as const;

type SentryErrorBoundaryProps = ComponentProps<typeof SentryErrorBoundary>;

export type ErrorBoundaryProps = {
  /** optional unique identifier for the error boundary to make it more easily identifiable and findable */
  id?: string;
  /**
   * Tier tracks the blast radius of a triggered error boundary. Ideally, the blast radius is as small as possible.
   * - app: White screen of death, e.g. uncaught error in root component or error not caught by any lower boundary.
   *    Ideally never happens because a lower boundary should catch errors before they bubble up to this level.
   * - route: Error that causes an entire route to fail. Not great because an entire route is broken, but contained to a specific route.
   * - feature: Error contained within a specific feature. Doesn't take down an entire route, impact is limited to a specific feature or flow.
   *
   * This drives alerting rules in Sentry.
   */
  tier: BoundaryTier;
  /**
   * A free-form string indicating what feature the error boundary is wrapping.
   * Helpful for debugging and alerting so we can quickly identify what part of the app is impacted.
   */
  feature: string;
  /**
   * The team that owns the code wrapped by this error boundary.
   * Used for routing alerts in Sentry.
   */
  team: string;
  /**
   * When any value in this array changes while the boundary is in an error state, the error boundary resets which gives the UI an opportunity
   * to re-render and potentially clear the error. Has no effect during normal (non-error) rendering. Pass values like the current route
   * pathname to auto-reset on navigation.
   *
   * @example
   * const { pathname } = useLocation();
   * <ErrorBoundary resetKeys={[pathname]} tier='route' feature='dashboard' team='my-team'>
   */
  resetKeys?: (string | number | boolean | null | undefined)[];
  children?: React.ReactNode;
} & Omit<SentryErrorBoundaryProps, 'beforeCapture'>;

export function ErrorBoundary({ id, tier, feature, team, resetKeys, children, ...props }: ErrorBoundaryProps) {
  const hasFallback = props.fallback != null;
  const hasErrorRef = useRef(false);
  const [resetCount, setResetCount] = useState(0);

  const resetKeySignal = resetKeys ? JSON.stringify(resetKeys) : undefined;
  useEffect(() => {
    if (hasErrorRef.current) {
      hasErrorRef.current = false;
      // only bump the key (which causes a remount) when resetKeys change while in error state
      setResetCount((c) => c + 1);
    }
  }, [resetKeySignal]);

  return (
    <SentryErrorBoundary
      key={resetCount}
      {...props}
      onError={(error, componentStack, eventId) => {
        hasErrorRef.current = true;
        props.onError?.(error, componentStack, eventId);
      }}
      onReset={(...args) => {
        hasErrorRef.current = false;
        props.onReset?.(...args);
      }}
      beforeCapture={(scope) => {
        // a lot of error boundaries have this topic tag on it, preserve it for historical purposes
        scope.setTag('topic', 'errorBoundary');

        scope.setTag('team', team); // team tag is mapped in Sentry

        scope.setTag('boundary.tier', tier);
        scope.setTag('boundary.feature', feature);
        scope.setTag('boundary.hasFallback', hasFallback);
        if (!!id) scope.setTag('boundary.id', id);

        scope.setContext('meta', { sampleRate: 1 }); // override the global sample rate for error boundaries so we track 100% of them

        scope.setLevel(tierToLevel[tier]);
      }}
    >
      {children}
    </SentryErrorBoundary>
  );
}
// displayName is different so it's easier to identify error boundaries in React DevTools and to differentiate from the SentryErrorBoundary component in stack traces
ErrorBoundary.displayName = 'WeaveWrappedErrorBoundary';
